import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogIn, MailQuestion } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      toast.success("If that email is registered, a reset link is on its way");
      setMode("login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-sand grain py-20" data-testid="admin-login-page">
      <div className="mx-auto max-w-md px-6">
        <Reveal>
          <div className="rounded-[2rem] border border-coal/5 bg-white p-8 shadow-[0_24px_60px_-24px_rgba(26,29,36,0.35)]">
            <Eyebrow>Team access</Eyebrow>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-coal">
              {mode === "login" ? "Admin login" : "Reset password"}
            </h1>
            <p className="mt-2 text-sm text-coal-muted">
              {mode === "login"
                ? "For the EduPower team to manage photos and comments."
                : "Enter the admin email and we will send a reset link."}
            </p>

            {error && (
              <p className="mt-4 rounded-xl bg-ember-subtle px-4 py-3 text-sm font-semibold text-ember" data-testid="login-error">
                {error}
              </p>
            )}

            <form onSubmit={mode === "login" ? submitLogin : submitForgot} className="mt-6 space-y-4">
              <div>
                <label htmlFor="admin-email" className="mb-1.5 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">
                  Email
                </label>
                <input
                  id="admin-email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-coal/15 bg-sand px-4 py-3 text-sm text-coal outline-none transition-colors focus:border-teal"
                  placeholder="admin@edupowerfoundation.org"
                />
              </div>
              {mode === "login" && (
                <div>
                  <label htmlFor="admin-password" className="mb-1.5 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">
                    Password
                  </label>
                  <input
                    id="admin-password"
                    data-testid="login-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-coal/15 bg-sand px-4 py-3 text-sm text-coal outline-none transition-colors focus:border-teal"
                    placeholder="Your password"
                  />
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="login-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-ember px-6 py-3 font-heading text-sm font-bold text-sand transition-colors hover:bg-teal disabled:opacity-60"
              >
                {mode === "login" ? <LogIn size={16} /> : <MailQuestion size={16} />}
                {busy ? "One moment..." : mode === "login" ? "Log in" : "Send reset link"}
              </button>
            </form>

            <button
              onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setError(""); }}
              data-testid="toggle-forgot-password"
              className="mt-4 w-full text-center text-xs font-semibold text-teal transition-colors hover:text-ember"
            >
              {mode === "login" ? "Forgot your password?" : "Back to login"}
            </button>
          </div>
        </Reveal>
        <p className="mt-6 text-center text-xs text-coal-muted">
          Not part of the team? <Link to="/" className="font-bold text-teal hover:text-ember">Back to the site</Link>
        </p>
      </div>
    </div>
  );
}
